import React, { useState, useRef, useEffect } from 'react';
import '../styles.css';

// Each variant folder is named for its actual pixel width.
const SIZES = [480, 960, 1600, 2560];
const toImagePath = (width, imageId) => `photos/w${width}/${imageId}.webp`;

const buildSrcSet = (imageId) => SIZES
  .map((width) => `${toImagePath(width, imageId)} ${width}w`)
  .join(', ');

// In the windowed (non-fullscreen) carousel the image is roughly viewport
// width minus the sidebar; tell the browser so it can pick the smallest file.
const imageSizes = '(max-width: 768px) 94vw, (max-width: 1100px) 84vw, 72vw';
const fullScreenSizes = '100vw';
const SWIPE_THRESHOLD_PX = 45;

const ImageCarousel = ({ imageIds, initialIndex, onIndexChange, isDarkMode }) => {
  const carouselRef = useRef(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.focus();
    }
  }, []);

  const [currentIndex, setCurrentIndex] = useState(initialIndex || 0);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    onIndexChange(currentIndex);
  }, [currentIndex, onIndexChange]);

  useEffect(() => {
    setCurrentIndex(initialIndex || 0);
  }, [initialIndex]);

  useEffect(() => {
    if (!imageIds.length) {
      return;
    }

    const preloadImage = (index) => {
      const imageId = imageIds[index];
      const preloadedImage = new Image();
      preloadedImage.sizes = imageSizes;
      preloadedImage.srcset = buildSrcSet(imageId);
      preloadedImage.decoding = 'async';
      // Setting `src` after `srcset` makes some browsers actually honor the
      // responsive selection rather than fetching the fallback.
      preloadedImage.src = toImagePath(960, imageId);
    };

    // Immediately preload adjacent images
    const nextIndex = (currentIndex + 1) % imageIds.length;
    const previousIndex = (currentIndex - 1 + imageIds.length) % imageIds.length;
    preloadImage(nextIndex);
    preloadImage(previousIndex);

    // Preload 2nd-ahead neighbors during idle time
    const scheduleIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 100));
    const cancelIdle = window.cancelIdleCallback || clearTimeout;
    const idleHandle = scheduleIdle(() => {
      preloadImage((currentIndex + 2) % imageIds.length);
      preloadImage((currentIndex - 2 + imageIds.length) % imageIds.length);
    }, { timeout: 1000 });

    return () => cancelIdle(idleHandle);
  }, [currentIndex, imageIds]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? imageIds.length - 1 : prevIndex - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === imageIds.length - 1 ? 0 : prevIndex + 1));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      goToPrevious();
    } else if (event.key === 'ArrowRight') {
      goToNext();
    } else if ((event.key === 'Escape') && isFullScreen) {
      setIsFullScreen(false);
    }
  };

  const handleImageClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    setIsFullScreen(true);
  };

  const handleFullScreenImageClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    setIsFullScreen(false);
  };

  const handleCloseClick = () => {
    setIsFullScreen(false);
  };

  const handleBlur = () => {
    if (carouselRef.current) {
      carouselRef.current.focus();
    }
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) {
      return;
    }

    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    if (absDeltaX < SWIPE_THRESHOLD_PX || absDeltaX <= absDeltaY) {
      return;
    }

    suppressClickRef.current = true;

    if (deltaX < 0) {
      goToNext();
    } else {
      goToPrevious();
    }
  };

  if (!imageIds.length) {
    return null;
  }

  const currentImageId = imageIds[currentIndex];
  // The browser picks the matching variant from `srcSet` based on `sizes` and
  // DPR; `src` is just the fallback for very old browsers, so default it to
  // the small file to keep the request light if srcSet ever fails to apply.
  const standardImage = toImagePath(960, currentImageId);
  const fullScreenImage = toImagePath(2560, currentImageId);
  const srcSet = buildSrcSet(currentImageId);

  return (
    <div className="carousel-container">
      <div
        className="carousel"
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        tabIndex="0"
        ref={carouselRef}
        style={{ outline: 'none' }}
      >
        {!isFullScreen && (
          <>
            <button
              className={`carousel-button left ${isDarkMode ? 'dark-mode' : ''}`}
              onClick={goToPrevious}
              aria-label="Previous image"
            >
              &lt;
            </button>
            <img
              src={standardImage}
              srcSet={srcSet}
              sizes={imageSizes}
              alt=""
              className={`carousel-image ${isDarkMode ? 'dark-mode' : ''}`}
              onClick={handleImageClick}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <button
              className={`carousel-button right ${isDarkMode ? 'dark-mode' : ''}`}
              onClick={goToNext}
              aria-label="Next image"
            >
              &gt;
            </button>
          </>
        )}

        {isFullScreen && (
          <div className={`full-screen-overlay ${isDarkMode ? 'dark-mode' : ''}`} onKeyDown={handleKeyDown} tabIndex="0">
            <button className={`close-button ${isDarkMode ? 'dark-mode' : ''}`} onClick={handleCloseClick}>X</button>
            <button className={`carousel-button left ${isDarkMode ? 'dark-mode' : ''}`} onClick={goToPrevious}>&lt;</button>
            <img
              src={fullScreenImage}
              srcSet={srcSet}
              sizes={fullScreenSizes}
              alt=""
              className={`full-screen-image ${isDarkMode ? 'dark-mode' : ''}`}
                onClick={handleFullScreenImageClick}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <button className={`carousel-button right ${isDarkMode ? 'dark-mode' : ''}`} onClick={goToNext}>&gt;</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageCarousel;
